import { Contact, NewDeal, Organization, Product, User } from 'types'
import { updateContractTimelineData } from 'components/Timeline/createTimelineData'
import { useCreateContract } from 'api/PandaDoc/useCreateContract'
import { useCreateTimeline } from 'api/aws/useCreateTimeline'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useQueryClient } from '@tanstack/react-query'
import { SetStateAction } from 'react'
import { useDealUpdate } from 'api/aws/useEntityUpdate'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'

export const useCreateDocuments = () => {
  const { mutate: createDocument } = useCreateContract()
  const { deals, products } = useEntitiesFromSearch()
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const { mutate: updateDeal } = useDealUpdate()
  const { syncDealFields } = useSyncQueryCache()

  const createInternalContract = async (
    setIsCreating: React.Dispatch<SetStateAction<boolean>>,
    newDeal: NewDeal,
    currAuthRecipient: User,
    currContactRecipient: Contact,
    activeUser: User,
    organization: Organization,
    productRows: Product[],
    totals?
  ) => {
    // setIsCreating(true)
    const rawPredecessorDeal = deals?.find(d => d?.deal_uuid === newDeal?.predecessorId) ?? null
    const predecessorProducts = products?.filter(p => p?.deal_uuid === rawPredecessorDeal?.deal_uuid) ?? []
    const predecessorDeal = { ...rawPredecessorDeal, products: predecessorProducts ?? [] }
    const isInternalUpdateRequired = predecessorDeal
      ? newDeal?.status === 'Completed Internally' && rawPredecessorDeal?.dealStatus !== 'Replaced'
      : false

    const cleanedDeal = {
      ...newDeal,
      creation_timestamp: new Date(),
      isCreated: true,
      recipients: {
        authorizedRecipient: newDeal?.recipients?.authorizedRecipient?.map(rec => ({
          ...rec,
          contact_id: currAuthRecipient?.panda_contact_id || currAuthRecipient?.contact_id,
        })),
        contactRecipient: newDeal?.recipients?.contactRecipient?.map(rec => ({
          ...rec,
          contact_id: currContactRecipient?.panda_contact_id || currContactRecipient?.contact_id,
        })),
      },
    }
    console.log('cleanedDeal', cleanedDeal)
    const documentData = {
      deal: cleanedDeal,
      products: productRows,
      organization,
      activeUser,
      ...(isInternalUpdateRequired
        ? { isUpdateRequired: isInternalUpdateRequired, predecessorDeal: predecessorDeal ?? {} }
        : {}),
    }
    console.log('documentData', documentData)

    try {
      const timelineData = updateContractTimelineData(
        cleanedDeal?.deal_uuid,
        activeUser,
        'Create',
        'deals',
        'Contract created'
      )

      // Then, create the PandaDoc document
      const documentData = {
        deal: cleanedDeal,
        products: productRows,
        organization,
        activeUser,
        ...(isInternalUpdateRequired
          ? { isUpdateRequired: isInternalUpdateRequired, predecessorDeal: predecessorDeal ?? {} }
          : {}),
      }

      createDocument(documentData, {
        onSuccess: data => {
          console.log('[createDocument onSuccess] Data', data)
          const contractId = data?.id
          const hasProducts = productRows?.length > 0
          // Update deal with contract ID, draft status, linksGenerated = false
          const dealWithContractId = {
            ...cleanedDeal,
            contractId: contractId,
            status: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.status : 'Created',
            stage: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.stage : 'Contract Created',
            isDraft: true,
            isReverted: false,
            linksGenerated: false,
            lastModified: new Date(),
            ...totals,
            hasProducts: hasProducts,
            products: productRows,
            totals: { ...totals },
          }
          console.log('🟡 Saving deal with contractId and status update:', dealWithContractId)
          // Save again with contractId
          updateDeal(
            {
              PK: dealWithContractId.PK,
              SK: dealWithContractId.SK,
              dealData: dealWithContractId,
            },
            {
              onSuccess: () => {
                setIsCreating(false)
                createTimelineEntry(timelineData)
                syncDealFields(queryClient, cleanedDeal, [
                  { fieldName: 'contractId', newValue: contractId },
                  {
                    fieldName: 'status',
                    newValue: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.status : 'Created',
                  },
                  {
                    fieldName: 'stage',
                    newValue: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.stage : 'Contract Created',
                  },
                  { fieldName: 'isDraft', newValue: true },
                  { fieldName: 'isReverted', newValue: false },
                  { fieldName: 'isCreated', newValue: true },
                  { fieldName: 'linksGenerated', newValue: false },
                  { fieldName: 'lastModified', newValue: new Date() },
                  { fieldName: 'totals', newValue: totals },
                  { fieldName: 'hasProducts', newValue: hasProducts },
                ])
                showSnackbar('Document successfully created', 'success')
              },
              onError: err => {
                console.error('❌ Failed to update deal with contractId:', err)
                setIsCreating(false)
              },
            }
          )
        },
        onError: err => {
          setIsCreating(false)
          console.error('❌ PandaDoc creation failed:', err)
          showSnackbar('Error creating PandaDoc document', 'error')
        },
      })
    } catch (err) {
      setIsCreating(false)
      console.error('❌ Unexpected error in handleSubmit:', err)
    }
  }
  //   const handleSubmit = async e => {}
  return { createInternalContract }
}
